// apps/landing-page/src/lib/analytics.ts
import { ensureAnonId } from './anon-id';
import { CONSENT_CHANGE_EVENT, hasAnalyticsConsent } from './consent';

/**
 * Client-side event types this SDK emits — a subset of the server's
 * ANALYTICS_EVENT_TYPES allowlist (apps/api/.../analytics-events.types.ts).
 * Kept as its own literal union rather than importing server code into the
 * browser bundle; extend both lists together when a new client emitter ships.
 */
type ClientEventType = 'page.viewed' | 'product.viewed' | 'results.viewed' | 'recommendation.clicked' | 'search.performed';

/** Mirrors cart-sync.ts's SyncEntityType — the same product-kind vocabulary. */
export type ProductEntityType = 'TEST_MODULE' | 'BUNDLE' | 'COURSE';

interface ClientEvent {
  type: ClientEventType;
  entityType?: string;
  entityId?: string;
  value?: number;
  metadata?: Record<string, unknown>;
}

// AnalyticsEvent.source — see docs/analytics-data-platform.md §2.1's
// "web-dashboard" | "web-landing" | "api" vocabulary.
const SOURCE = 'web-landing';

// Buffer briefly so a burst of events on the same navigation (e.g. a
// page.viewed alongside a product.viewed) becomes one POST /v1/events call
// instead of several — the architecture doc's "tiny shared emitter, buffers
// and flushes on a timer" (§3), sized well under the server's 50-event cap.
const FLUSH_DELAY_MS = 1500;
const MAX_BUFFER_SIZE = 20;

let buffer: ClientEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function flush(): void {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (buffer.length === 0) return;

  const events = buffer;
  buffer = [];

  // Re-check at send time: consent may have been revoked (or reset) after these
  // events were buffered but before the debounce fired. Drop them, and never
  // reach ensureAnonId(), so a revoke stops collection immediately.
  if (!hasAnalyticsConsent()) return;

  const anonId = ensureAnonId();
  fetch('/api/proxy/events', {
    method: 'POST',
    credentials: 'include',
    // Lets the request survive a navigation/tab-close that happens right after
    // it's issued — the visibilitychange flush below is what actually fires it
    // in that case, keepalive just keeps it alive through the unload.
    keepalive: true,
    headers: {
      'Content-Type': 'application/json',
      'x-anon-id': anonId,
      'x-analytics-source': SOURCE,
    },
    body: JSON.stringify({ events }),
  }).catch(() => {});
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(flush, FLUSH_DELAY_MS);
}

function enqueue(event: ClientEvent): void {
  if (typeof document === 'undefined') return; // no-op during SSR/build

  // Analytics-consent gate (docs/analytics-data-platform.md §6) — the single choke
  // point both public trackers below funnel through. Absent/denied consent (or a
  // browser DNT/GPC signal — see ./consent) means nothing is buffered, so flush()
  // never runs for real data and, critically, never reaches ensureAnonId(): no
  // event is emitted AND no aks_anon cookie gets minted on this call path.
  if (!hasAnalyticsConsent()) return;

  buffer.push(event);
  if (buffer.length >= MAX_BUFFER_SIZE) {
    flush();
    return;
  }
  scheduleFlush();
}

// Drop anything buffered the moment consent is withdrawn or reset (flush() re-checks too,
// this just frees the buffer and the timer promptly).
if (typeof window !== 'undefined') {
  window.addEventListener(CONSENT_CHANGE_EVENT, () => {
    if (hasAnalyticsConsent()) return;
    buffer = [];
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
  });
}

// A pending debounce timer would otherwise lose the batch on tab close/navigation —
// flush eagerly the moment the tab is hidden rather than waiting out FLUSH_DELAY_MS.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
}

/** Fires once per distinct page view. Callers dedupe (see PageViewTracker) — this never does. */
export function trackPageView(path: string, search?: string): void {
  enqueue({ type: 'page.viewed', metadata: search ? { path, search } : { path } });
}

/**
 * Fires once per distinct product detail view. Callers dedupe (see useProductViewTracking).
 * `source` is optional attribution for WHERE the viewer came from before landing on this
 * product (e.g. "after-free-tryout" from a test-engine result/pembahasan CTA — see
 * ?src= query param convention, read by the caller and threaded through
 * useProductViewTracking). Landed in metadata.source, not a new top-level column, since
 * it's one more descriptive attribute of the view, same as entityType/value.
 */
export function trackProductView(
  entityType: ProductEntityType,
  entityId: string,
  value?: number,
  source?: string,
): void {
  enqueue({ type: 'product.viewed', entityType, entityId, value, metadata: source ? { source } : undefined });
}

/**
 * Generic emitter for event types that don't (yet) warrant their own named
 * tracker — e.g. results.viewed, recommendation.clicked. Same consent-gated,
 * buffered, fire-and-forget path as the specific trackers above; callers
 * dedupe their own way, this never does.
 */
export function trackEvent(type: ClientEventType, opts?: Omit<ClientEvent, 'type'>): void {
  enqueue({ type, ...opts });
}

/** Test-only escape hatch — vitest has no real navigation/unload to trigger a flush. */
export function __flushAnalyticsForTest(): void {
  flush();
}
